import { useTranslation } from "react-i18next";
import authEnglish from "../i18n/locales/en/auth";
import { LanguageMenu } from "../components/menus/LanguageMenu";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { SegmentedControl } from "../components/ui/SegmentedControl";
import { Alert } from "../components/ui/Alert";
import { ThemeSwitcher } from "../components/theme/ThemeSwitcher";
import { useAuth } from "../components/auth/AuthProvider";
import { AuthBrandingHeader } from "../components/auth/AuthBrandingHeader";
import { toast } from "../lib/toast";
import { trackEvent } from "../lib/auditCollector";
import { getPasskeySupport, passkeyLogin, type PasskeySupport } from "../lib/webauthn";
import { usePublicConfig } from "../hooks/usePublicConfig";
import { useBrandingSettings } from "../lib/settings/useBrandingSettings";
import { consumeStaleReloadFlag } from "../lib/versionCheck";

export const LoginPage = () => {
  const { t } = useTranslation("auth");
  const message = (key: Exclude<keyof typeof authEnglish, `${string}_one` | `${string}_other`>) => t($ => $[key]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { refresh, appConfig } = useAuth();
  const { data: publicConfig } = usePublicConfig();
  const branding = useBrandingSettings();
  const [mode, setMode] = useState("password");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [twofa, setTwofa] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Exclude<keyof typeof authEnglish, `${string}_one` | `${string}_other`> | null>(null);
  const [notice, setNotice] = useState<Exclude<keyof typeof authEnglish, `${string}_one` | `${string}_other`> | null>(null);
  const [passkeySupport, setPasskeySupport] = useState<PasskeySupport | null>(null);
  const [maintenanceMessage, setMaintenanceMessage] = useState<string | null>(null);
  const [activationMessage, setActivationMessage] = useState<Exclude<keyof typeof authEnglish, `${string}_one` | `${string}_other`> | null>(null);
  const [resetMessage, setResetMessage] = useState<Exclude<keyof typeof authEnglish, `${string}_one` | `${string}_other`> | null>(null);

  // Handle activation redirect from /auth/activate
  useEffect(() => {
    const activated = searchParams.get("activated");
    const reset = searchParams.get("reset");
    const next = new URLSearchParams(searchParams);
    let changed = false;

    if (activated === "success") {
      setActivationMessage("activated");
      next.delete("activated");
      changed = true;
    } else if (activated === "error") {
      setError("activationFailed");
      next.delete("activated");
      next.delete("reason");
      changed = true;
    } else if (activated === "invalid") {
      setError("invalidActivation");
      next.delete("activated");
      changed = true;
    }

    if (reset === "success") {
      setResetMessage("passwordReset");
      next.delete("reset");
      changed = true;
    }

    if (changed) {
      setSearchParams(next, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showTwoFaStep = useMemo(() => Boolean(pendingToken), [pendingToken]);
  const passkeySupported = useMemo(() => passkeySupport?.supported ?? false, [passkeySupport]);
  const registrationEnabled = useMemo(
    () => appConfig?.features.registrationEnabled ?? publicConfig?.registrationEnabled ?? true,
    [appConfig?.features.registrationEnabled, publicConfig?.registrationEnabled]
  );
  const authOptions = useMemo(
    () => (passkeySupported ? [{ id: "password", label: t($ => $.password) }, { id: "passkey", label: t($ => $.passkey) }] : [{ id: "password", label: t($ => $.password) }]),
    [passkeySupported, t]
  );

  useEffect(() => {
    let active = true;
    getPasskeySupport()
      .then((support) => {
        if (active) setPasskeySupport(support);
      })
      .catch(() => {
        if (active) {
          setPasskeySupport({
            supported: false,

            details: { secureContext: false, publicKeyCredential: false },
          });
        }
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    fetch("/api/system/maintenance", { headers: { Accept: "application/json" } })
      .then(async (res) => {
        const payload = await res.json().catch(() => null);
        if (!active) return;
        const maintenance = payload?.maintenance;
        if (maintenance?.enabled) {
          setMaintenanceMessage(maintenance.message ?? "");
        } else {
          setMaintenanceMessage(null);
        }
      })
      .catch(() => {
        if (active) setMaintenanceMessage(null);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    trackEvent({ category: "ui", action: "auth.login-attempt", component: "LoginPage", details: { method: "password" } });
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password, remember })
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 423 || data?.error?.code === "MAINTENANCE_MODE") {
        setError("maintenance");
        return;
      }
      if (data?.twoFactorRequired && data?.pendingToken) {
        setPendingToken(data.pendingToken);
        setNotice("finishVerification");
        return;
      }
      if (!res.ok) {
        setError("loginFailed");
        return;
      }
      await refresh();
      navigate(data?.redirectTo ?? "/profile", { replace: true });
    } catch {
      if (consumeStaleReloadFlag()) {
        setError("updated");
        setTimeout(() => window.location.reload(), 1500);
      } else {
        setError("network");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasskey = async () => {
    trackEvent({ category: "ui", action: "auth.login-attempt", component: "LoginPage", details: { method: "passkey" } });
    const support = passkeySupport;
    if (!support?.supported) {
      toast.error(t($ => $.passkeyUnavailable));
      return;
    }
    setLoading(true);
    try {
      const result = await passkeyLogin(username || undefined, remember);
      if (!result.ok) {
        toast.error(t($ => $.passkeyFailed));
        return;
      }
      await refresh();
      navigate(result.redirectTo ?? "/profile", { replace: true });
    } catch {
      toast.error(t($ => $.passkeyUnavailable));
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFa = async () => {
    trackEvent({ category: "ui", action: "auth.2fa-verify", component: "LoginPage", details: { method: useRecovery ? "recovery" : "totp" } });
    setNotice(null);
    setLoading(true);
    setError(null);
    try {
      if (!pendingToken) {
        setNotice("passwordFirst");
        return;
      }
      const res = await fetch("/api/auth/2fa/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pendingToken,
          code: useRecovery ? undefined : twofa,
          recoveryCode: useRecovery ? recoveryCode : undefined
        })
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 423 || data?.error?.code === "MAINTENANCE_MODE") {
        setError("maintenance");
        return;
      }
      if (!res.ok) {
        setError("verificationFailed");
        return;
      }
      await refresh();
      navigate(data?.redirectTo ?? "/profile", { replace: true });
    } catch {
      setError("network");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="surface-grid flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <AuthBrandingHeader
          title={t($ => $.welcome)}
          subtitle={t($ => $.subtitle)}
          authPages={publicConfig?.authPages}
          branding={branding.settings}
        />

        <Card className="glass-card">
          <div className="mb-5 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold">{t($ => $.signIn)}</h2>
              <p className="text-sm text-muted-foreground">{t($ => $.chooseMethod)}</p>
            </div>
            <LanguageMenu placement="auth" />
          </div>

          {maintenanceMessage !== null && (
            <Alert variant="warning" className="mb-4">
              {maintenanceMessage || t($ => $.maintenance)}
            </Alert>
          )}

          {activationMessage && (
            <Alert variant="success" className="mb-4">
              {message(activationMessage)}
            </Alert>
          )}

          {resetMessage && (
            <Alert variant="success" className="mb-4">
              {message(resetMessage)}
            </Alert>
          )}

          <SegmentedControl
            ariaLabel={t($ => $.chooseMethod)}
            options={authOptions}
            value={mode}
            onChange={(value) => {
              setMode(value);
              if (value !== "password") {
                setPendingToken(null);
              }
            }}
            className="mb-5"
          />

          {passkeySupport && !passkeySupport.supported && (
            <div className="mb-4 rounded-lg border border-border/60 bg-muted/60 px-4 py-3 text-xs text-muted-foreground">
              {t($ => $.passkeyUnavailable)}
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive-text">
              {message(error)}
            </div>
          )}

          {notice && (
            <div className="mb-4 rounded-lg border border-border/60 bg-muted/70 px-4 py-3 text-sm text-muted-foreground">
              {message(notice)}
            </div>
          )}

          {mode === "password" && (
            <form className="space-y-4" onSubmit={handleLogin}>
              <div className="space-y-1">
                <label htmlFor="login-username" className="text-xs uppercase tracking-wide text-muted-foreground">{t($ => $.username)}</label>
                <Input
                  id="login-username"
                  placeholder="you@example.com"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  autoComplete="username"
                  required
                />
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label htmlFor="login-password" className="text-xs uppercase tracking-wide text-muted-foreground">{t($ => $.password)}</label>
                  <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground">
                    {t($ => $.forgot)}
                  </Link>
                </div>
                <Input
                  id="login-password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>
              {(publicConfig?.session?.rememberEnabled ?? true) && (
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(event) => setRemember(event.target.checked)}
                    className="h-4 w-4 rounded border-border/70 bg-input text-primary focus-visible:ring-2 focus-visible:ring-ring"
                  />
                  {t($ => $.remember, { count: publicConfig?.session?.rememberDays ?? 7 })}
                </label>
              )}
              <Button type="submit" size="lg" className="w-full" disabled={loading}>
                {loading ? t($ => $.signingIn) : t($ => $.signIn)}
              </Button>
              {registrationEnabled && (
                <p className="text-center text-xs text-muted-foreground">
                  {t($ => $.needAccount)} <Link to="/register">{t($ => $.createAccount)}</Link>
                </p>
              )}
            </form>
          )}

          {mode === "passkey" && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{t($ => $.useRegisteredPasskey)}</p>
              <Button
                type="button"
                size="lg"
                className="w-full"
                onClick={handlePasskey}
                disabled={!passkeySupported || loading}
              >
                {t($ => $.usePasskey)}
              </Button>
              <p className="text-xs text-muted-foreground">
                {passkeySupported
                  ? t($ => $.passkeyPrompt)
                  : t($ => $.passkeyUnavailable)}
              </p>
            </div>
          )}

          {showTwoFaStep && (
            <div className="mt-6 space-y-4 rounded-lg border border-border/60 bg-muted/40 p-4">
              <div className="text-sm font-semibold">{t($ => $.twoFactor)}</div>
              <div className="space-y-2">
                <label htmlFor="login-verification" className="text-xs uppercase tracking-wide text-muted-foreground">
                  {useRecovery ? t($ => $.recovery) : t($ => $.verification)}
                </label>
                <Input
                  id="login-verification"
                  placeholder={useRecovery ? "ABCD-EFGH-IJKL" : "123 456"}
                  value={useRecovery ? recoveryCode : twofa}
                  onChange={(event) =>
                    useRecovery ? setRecoveryCode(event.target.value) : setTwofa(event.target.value)
                  }
                  autoComplete={useRecovery ? "one-time-code" : "one-time-code"}
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  checked={useRecovery}
                  onChange={(event) => setUseRecovery(event.target.checked)}
                  className="h-4 w-4 rounded border-border/70 bg-input text-primary focus-visible:ring-2 focus-visible:ring-ring"
                />
                {t($ => $.useRecovery)}
              </label>
              <Button type="button" size="lg" className="w-full" onClick={handleTwoFa} disabled={loading}>
                {t($ => $.verify)}
              </Button>
              <p className="text-xs text-muted-foreground">
                {t($ => $.enterCode)}
              </p>
            </div>
          )}
        </Card>
        <div className="mt-4 flex justify-center">
          <ThemeSwitcher compact />
        </div>
      </div>
    </div>
  );
};
